import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  MessageSquare,
  Sparkles,
  Mic,
  Bookmark,
  Briefcase,
  Coffee,
  ShoppingBag,
  Hotel,
  Headphones,
  Building2,
  Heart,
  ChevronRight,
  Zap,
} from "lucide-react";
import { AppShell, PilotDisclaimer } from "@/components/AppShell";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { listScenarios } from "@/lib/api";

export default function Welcome() {
  const { lang, tr } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [previewScenarios, setPreviewScenarios] = useState([]);

  useEffect(() => {
    listScenarios()
      .then((sc) => setPreviewScenarios((sc || []).slice(0, 3)))
      .catch(() => setPreviewScenarios([]));
  }, []);

  const goStart = () => navigate(user ? "/home" : "/auth?mode=signup");
  const goLogin = () => navigate("/auth?mode=signin");
  const goPractice = () => navigate(user ? "/practice?browse=all" : "/auth?mode=signup");

  const bubble1 = lang === "en" ? "You can do this. Let's practice." : "Tú puedes. Vamos a practicar.";
  const bubble2 = lang === "en" ? "Practice real conversations." : "Practica conversaciones reales.";
  const bubble3 = lang === "en" ? "Speak with confidence." : "Habla con confianza.";
  const handwritten = lang === "en" ? "Practice today. Feel ready tomorrow." : "Practica hoy. Siéntete listo mañana.";
  const stripCopy = lang === "en" ? "More practice. More confidence. More opportunities." : "Más práctica. Más confianza. Más oportunidades.";
  const finalHeading = lang === "en" ? "Your next conversation can go better." : "Tu próxima conversación puede salir mejor.";
  const finalBody = lang === "en"
    ? "Try ListoTalk AI free during the pilot and rehearse the real English you use at work."
    : "Prueba ListoTalk AI gratis durante el piloto y ensaya el inglés real que usas en el trabajo.";

  return (
    <AppShell hideNav>
      <div data-testid="welcome-screen">
        {/* HERO — split layout */}
        <section className="wpe-hero-gradient relative px-5 pt-6 pb-10 overflow-hidden">
          <span className="wpe-hero-blob" style={{ top: "-30px", left: "-40px", width: "220px", height: "220px", background: "rgba(20, 184, 166, 0.42)" }} />
          <span className="wpe-hero-blob" style={{ top: "20%", right: "-40px", width: "180px", height: "180px", background: "rgba(251, 191, 36, 0.38)" }} />
          <span className="wpe-hero-blob" style={{ bottom: "-40px", left: "40%", width: "220px", height: "220px", background: "rgba(249, 115, 96, 0.28)" }} />
          <div className="relative flex flex-col items-center gap-6">
            {/* LEFT — copy + CTA */}
            <div className="w-full space-y-4">
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/80 backdrop-blur text-[11px] font-semibold text-[rgb(var(--wpe-teal-hover))] border border-[rgb(var(--wpe-teal-light))]"
                data-testid="pilot-badge"
              >
                <Sparkles size={12} /> {tr("pilotBadge")}
              </span>
              <h1
                className="text-[30px] leading-[1.05] sm:text-[46px] font-extrabold tracking-tight text-[rgb(var(--wpe-navy))]"
                data-testid="welcome-title"
              >
                {tr("landingHeroTitle")}
              </h1>
              <p
                className="text-[15px] sm:text-[16px] text-[rgb(var(--wpe-slate))] leading-relaxed max-w-md"
                data-testid="welcome-hero-body"
              >
                {tr("landingHeroBody")}
              </p>
              <p className="wpe-handwritten pt-1" aria-hidden>
                {handwritten}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 pt-2">
                <button
                  type="button"
                  className="wpe-cta wpe-cta-lg"
                  onClick={goStart}
                  data-testid="welcome-start-trial-btn"
                >
                  {tr("landingPrimaryCta")} <ArrowRight size={18} />
                </button>
                {!user && (
                  <button
                    type="button"
                    className="wpe-secondary bg-white/70 backdrop-blur"
                    onClick={goLogin}
                    data-testid="welcome-auth-btn"
                  >
                    {tr("landingSecondaryCta")}
                  </button>
                )}
              </div>
            </div>

            {/* RIGHT — real adult portrait (face fully visible) with speech bubbles
                stacked BELOW the photo so they can never overlap the face nor each
                other, regardless of ES/EN text length. */}
            <div className="relative w-full" data-testid="landing-hero-visual">
              <div className="relative aspect-square w-full max-w-[240px] mx-auto">
                <div className="absolute inset-0 rounded-[36px] overflow-hidden shadow-lg ring-4 ring-white/70 bg-white">
                  <img
                    src="https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=720&h=720&crop=faces&facepad=2&q=80"
                    alt=""
                    aria-hidden="true"
                    loading="eager"
                    decoding="async"
                    className="w-full h-full object-cover object-top select-none pointer-events-none"
                    data-testid="landing-hero-photo"
                  />
                </div>
              </div>
              {/* Bubbles live in normal flex flow (never absolute), so they cannot
                  overlap. Alternating self-start / self-end keeps the visual
                  rhythm of the old design. */}
              <div
                className="mt-5 flex flex-col items-stretch gap-2"
                data-testid="landing-hero-bubbles"
              >
                <div
                  className="wpe-speech-bubble wpe-speech-bubble-teal self-start max-w-full"
                  data-testid="hero-bubble-1"
                >
                  {bubble1}
                </div>
                <div
                  className="wpe-speech-bubble wpe-speech-bubble-coral self-end max-w-full"
                  data-testid="hero-bubble-2"
                >
                  {bubble2}
                </div>
                <div
                  className="wpe-speech-bubble wpe-speech-bubble-gold self-start max-w-full"
                  data-testid="hero-bubble-3"
                >
                  {bubble3}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 4 colorful value cards */}
        <section className="px-5 py-8 bg-white space-y-4">
          <div className="space-y-1">
            <span className="wpe-eyebrow-teal">{lang === "en" ? "How it works" : "Cómo funciona"}</span>
            <h2 className="wpe-section-title">
              {lang === "en" ? "Rehearse the moments that matter" : "Ensaya los momentos que importan"}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <BigValueCard icon={<MessageSquare size={22} />} title={tr("landingSection1Title")} body={tr("landingSection1Body")} accent="coral" testid="landing-section-1" />
            <BigValueCard icon={<Mic size={22} />} title={tr("landingSection2Title")} body={tr("landingSection2Body")} accent="teal" testid="landing-section-2" />
            <BigValueCard icon={<Sparkles size={22} />} title={tr("landingSection3Title")} body={tr("landingSection3Body")} accent="gold" testid="landing-section-3" />
            <BigValueCard icon={<Bookmark size={22} />} title={tr("landingSection4Title")} body={tr("landingSection4Body")} accent="blue" testid="landing-section-4" />
          </div>
        </section>

        {/* Motivational strip */}
        <section className="wpe-section-mint px-5 py-6" data-testid="landing-motiv-strip">
          <div className="max-w-2xl mx-auto flex items-center gap-3">
            <span className="w-11 h-11 rounded-2xl wpe-chip-teal flex items-center justify-center shrink-0">
              <Zap size={20} />
            </span>
            <p className="text-[15px] sm:text-[16px] font-bold text-[rgb(var(--wpe-navy))] leading-snug">
              {stripCopy}
            </p>
          </div>
        </section>

        {/* Situations preview */}
        <section className="px-5 py-9 bg-white space-y-4" data-testid="landing-situations-section">
          <div className="flex items-end justify-between gap-3">
            <div className="space-y-1">
              <span className="wpe-eyebrow-coral">{lang === "en" ? "Real workplace situations" : "Situaciones reales"}</span>
              <h2 className="wpe-section-title">
                {lang === "en" ? "Situations you can practice" : "Situaciones de práctica"}
              </h2>
            </div>
            <button
              type="button"
              onClick={goPractice}
              className="text-[12px] font-semibold text-[rgb(var(--wpe-teal-hover))] inline-flex items-center gap-0.5 whitespace-nowrap"
              data-testid="landing-see-all-situations-btn"
            >
              {lang === "en" ? "See all" : "Ver todas"} <ChevronRight size={14} />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {previewScenarios.length === 0 && (
              <>
                <SituationPreviewCard title={lang === "en" ? "Take a customer's order" : "Tomar el pedido de un cliente"} category={lang === "en" ? "Restaurant" : "Restaurante"} accent="coral" icon={<Coffee size={22} />} onClick={goPractice} />
                <SituationPreviewCard title={lang === "en" ? "Handle a wrong order" : "Manejar un pedido incorrecto"} category={lang === "en" ? "Restaurant" : "Restaurante"} accent="gold" icon={<ShoppingBag size={22} />} onClick={goPractice} />
                <SituationPreviewCard title={lang === "en" ? "Check in a guest" : "Registrar a un huésped"} category={lang === "en" ? "Hospitality" : "Hospitalidad"} accent="blue" icon={<Hotel size={22} />} onClick={goPractice} />
              </>
            )}
            {previewScenarios.map((s, idx) => (
              <SituationPreviewCard
                key={s.id}
                title={lang === "en" ? s.title_en : s.title_es}
                category={lang === "en" ? s.category_en : s.category_es}
                accent={["coral", "gold", "blue", "teal", "lavender"][idx % 5]}
                icon={<CategoryIcon category={s.category_en} />}
                onClick={goPractice}
              />
            ))}
          </div>
        </section>

        {/* Built for */}
        <section className="wpe-section-cream px-5 py-8 space-y-4">
          <div className="space-y-1 max-w-2xl mx-auto">
            <span className="wpe-eyebrow-gold">{lang === "en" ? "Who it's for" : "Para quién"}</span>
            <h2 className="wpe-section-title">
              {lang === "en" ? "Built for people who use English on the job" : "Creado para quienes usan inglés en el trabajo"}
            </h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-w-2xl mx-auto">
            <JobChip icon={<Coffee size={16} />} label={lang === "en" ? "Restaurant" : "Restaurante"} accent="coral" />
            <JobChip icon={<ShoppingBag size={16} />} label={lang === "en" ? "Retail" : "Comercio"} accent="gold" />
            <JobChip icon={<Hotel size={16} />} label={lang === "en" ? "Hotel" : "Hotel"} accent="blue" />
            <JobChip icon={<Building2 size={16} />} label={lang === "en" ? "Warehouse" : "Almacén"} accent="teal" />
            <JobChip icon={<Headphones size={16} />} label={lang === "en" ? "Customer service" : "Servicio al cliente"} accent="lavender" />
            <JobChip icon={<Heart size={16} />} label={lang === "en" ? "Healthcare support" : "Apoyo en salud"} accent="coral" />
          </div>
        </section>

        {/* Final motivational banner */}
        <section className="px-5 py-10 bg-white">
          <div className="wpe-motiv-banner text-center space-y-4" data-testid="landing-closing-banner">
            <Sparkles size={30} className="mx-auto opacity-95" />
            <h3 className="text-[24px] sm:text-[28px] font-extrabold leading-tight">
              {finalHeading}
            </h3>
            <p className="text-[13px] sm:text-[14px] text-white/90 max-w-md mx-auto">
              {finalBody}
            </p>
            <button
              type="button"
              onClick={goStart}
              className="wpe-cta wpe-hero-cta-inv inline-flex mt-2"
              data-testid="landing-closing-cta"
            >
              {tr("landingPrimaryCta")} <ArrowRight size={16} />
            </button>
          </div>
        </section>

        <div className="px-5 pb-8">
          <PilotDisclaimer />
        </div>
      </div>
    </AppShell>
  );
}

const CHIP_CLASS = {
  teal: "wpe-chip-teal",
  coral: "wpe-chip-coral",
  gold: "wpe-chip-gold",
  blue: "wpe-chip-blue",
  lavender: "wpe-chip-lavender",
};

const CARD_CLASS = {
  teal: "wpe-card-mint",
  coral: "wpe-card-coral",
  gold: "wpe-card-gold",
  blue: "wpe-card-blue",
  lavender: "wpe-card-lavender",
};

const EYE_CLASS = {
  teal: "wpe-eyebrow-teal",
  coral: "wpe-eyebrow-coral",
  gold: "wpe-eyebrow-gold",
  blue: "wpe-eyebrow-blue",
  lavender: "wpe-eyebrow-lavender",
};

const BigValueCard = ({ icon, title, body, accent = "teal", testid }) => (
  <div className={`wpe-card ${CARD_CLASS[accent]} wpe-glow-card space-y-3`} data-testid={testid}>
    <span className={`inline-flex items-center justify-center w-12 h-12 rounded-2xl ${CHIP_CLASS[accent]}`}>
      {icon}
    </span>
    <div>
      <div className="text-[16px] font-bold text-[rgb(var(--wpe-navy))] leading-snug">
        {title}
      </div>
      <div className="text-[13px] text-[rgb(var(--wpe-slate))] mt-1">{body}</div>
    </div>
  </div>
);

const SituationPreviewCard = ({ title, category, accent = "teal", icon, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`wpe-card ${CARD_CLASS[accent]} w-full text-left space-y-2 hover:-translate-y-0.5 transition-transform`}
    data-testid="landing-situation-preview-card"
  >
    <span className={`inline-flex items-center justify-center w-11 h-11 rounded-2xl ${CHIP_CLASS[accent]}`}>
      {icon}
    </span>
    <div>
      <div className={`${EYE_CLASS[accent]} mb-0.5`}>{category}</div>
      <div className="text-[14px] font-semibold text-[rgb(var(--wpe-navy))] leading-snug">
        {title}
      </div>
    </div>
  </button>
);

const JobChip = ({ icon, label, accent = "teal" }) => (
  <div className={`flex items-center gap-2 px-3 py-2.5 rounded-2xl ${CHIP_CLASS[accent]} font-semibold text-[13px]`}>
    {icon}
    <span className="min-w-0 truncate">{label}</span>
  </div>
);

const CategoryIcon = ({ category }) => {
  const c = (category || "").toLowerCase();
  if (c.includes("restaurant") || c.includes("restaur")) return <Coffee size={22} />;
  if (c.includes("hotel") || c.includes("hospital")) return <Hotel size={22} />;
  if (c.includes("retail") || c.includes("comerc")) return <ShoppingBag size={22} />;
  if (c.includes("call") || c.includes("service") || c.includes("servicio")) return <Headphones size={22} />;
  return <MessageSquare size={22} />;
};
